import React from 'react';
import { DEFAULT_COVER } from '@/constants/media';
import { REMOTE_FALLBACK_COVER_URL } from '@/constants/images';
import { resolveImageSrc } from '@/utils/media';

interface CoverImageProps {
  src?: string;
  alt: string;
}

const CoverImage: React.FC<CoverImageProps> = ({ src, alt }) => {
  // Normalize cover URL similarly to template preview logic
  const normalize = React.useCallback((u?: string) => {
    const s = String(u || '').trim();
    if (!s) return undefined as string | undefined;
    if (/^(https?:\/\/|blob:|data:)/i.test(s)) return s;
    if (s.includes('/uploads/')) return resolveImageSrc(s);
    if (s.startsWith('/images/') || s.startsWith('/assets/')) return s;
    const base = (import.meta as any).env?.VITE_APP_BASE_URL || (import.meta as any).env?.VITE_API_BASE_URL || '';
    if (!base) return s.startsWith('/') ? s : `/${s}`;
    return s.startsWith('/') ? `${String(base).replace(/\/$/, '')}${s}` : `${String(base).replace(/\/$/, '')}/${s}`;
  }, []);

  const candidate = normalize(src) || DEFAULT_COVER;
  const [resolved, setResolved] = React.useState<string>(candidate);
  const [triedNonApi, setTriedNonApi] = React.useState<boolean>(false);

  React.useEffect(() => {
    const next = normalize(src) || DEFAULT_COVER;
    let cancelled = false;
    try {
      const tester = document.createElement('img');
      tester.onload = () => { if (!cancelled) setResolved(next); };
      tester.onerror = () => {
        // Try local default, then remote fallback
        const def = document.createElement('img');
        def.onload = () => { if (!cancelled) setResolved(DEFAULT_COVER); };
        def.onerror = () => { if (!cancelled) setResolved(REMOTE_FALLBACK_COVER_URL); };
        def.src = DEFAULT_COVER;
      };
      tester.src = next;
    } catch {
      if (!cancelled) setResolved(REMOTE_FALLBACK_COVER_URL);
    }
    return () => { cancelled = true; };
  }, [src, normalize]);

  return (
    <div className="relative h-[200px] overflow-hidden rounded-t-2xl">
      <img
        src={resolved}
        alt={alt}
        className="absolute inset-0 w-full h-full object-cover"
        style={{ objectPosition: 'center 15%' }}
        onError={(e) => {
          const img = e.currentTarget as HTMLImageElement;
          // If API uploads path fails, try non-API '/uploads' once to leverage server fallback
          try {
            const url = new URL(img.src);
            const isApiUploads = /\/api\/uploads\//.test(url.pathname);
            if (isApiUploads && !triedNonApi) {
              setTriedNonApi(true);
              const nonApi = url.pathname.replace(/^\/api/, '');
              img.src = `${url.origin}${nonApi}`;
              return;
            }
          } catch {}
          if (img.src !== DEFAULT_COVER) {
            img.src = DEFAULT_COVER;
          } else {
            img.src = REMOTE_FALLBACK_COVER_URL;
          }
        }}
      />
      {/* Subtle bottom fade to separate sections without hiding the cover */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/10 pointer-events-none" />
    </div>
  );
};

export default CoverImage;